import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useIntl } from "react-intl";
import { syncStock } from "../Store/reducers/cartReducer";
import { notifyInfo } from "../utils/notify";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Re-checks stock for everything in the bag whenever `trigger` changes (the
 * bag opening, the checkout step changing) and tells the shopper what moved:
 * sold-out products get flagged, quantities above stock are reduced.
 */
export default function useCartStockSync(trigger, enabled = true) {
    const dispatch = useDispatch();
    const store = useStore();
    const intl = useIntl();
    const count = useSelector((state) => state.cart.products.length);
    const running = useRef(false);

    const sync = useCallback(async () => {
        const before = store.getState().cart.products;
        if (!before.length || running.current) return;
        running.current = true;
        try {
            const res = await fetch(`${API_URL}/products/availability`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    items: before.map((p) => ({ id: p.id, documentId: p.documentId })),
                }),
            });
            if (!res.ok) return;
            const { data } = await res.json();
            dispatch(syncStock(data));

            const after = store.getState().cart.products;
            for (const item of after) {
                const old = before.find((p) => p.id === item.id);
                if (!old) continue;
                if (item.unavailable && !old.unavailable) {
                    notifyInfo(intl.formatMessage({ id: "cart_item_sold_out" }, { title: item.title }));
                } else if (!item.unavailable && item.quantity < old.quantity) {
                    notifyInfo(
                        intl.formatMessage(
                            { id: "cart_item_reduced" },
                            { title: item.title, count: item.quantity }
                        )
                    );
                }
            }
        } catch {
            // Offline or API down: keep the bag as is, the order endpoint re-checks stock.
        } finally {
            running.current = false;
        }
    }, [dispatch, store, intl]);

    useEffect(() => {
        if (enabled && count > 0) sync();
        // Only re-run when the trigger changes, not on every quantity click.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trigger, enabled]);

    return sync;
}
